import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { Icon } from "../app/Icon";
import { inputClass } from "../components/FormKit";
import { usePagedView, ViewToggle, Pagination } from "../components/Paged";
import { initials } from "../profiles/ProfileMenu";
import {
  listMyOrgs, createOrg, getOrg, getMembers, inviteMember, setMemberRoles, removeMember, leaveOrg,
  ORG_ROLE_LABELS, ASSIGNABLE_ORG_ROLES, type Org, type OrgMember, type OrgInvite,
} from "../lib/orgs";

export function Organizations() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  async function loadOrgs() {
    setOrgs((await listMyOrgs()).organizations);
    setLoading(false);
  }
  useEffect(() => { void loadOrgs(); }, []);
  const { view, page, pageCount, pageItems, setView, setPage } = usePagedView(orgs, 12, "grid");

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { org_id } = await createOrg(name.trim());
      setName("");
      await loadOrgs();
      setSelId(org_id);
    } finally {
      setBusy(false);
    }
  }

  if (selId) return <OrgDetail orgId={selId} onBack={() => { setSelId(null); void loadOrgs(); }} />;

  return (
    <div className="grid gap-6">
      <PageHeader title="Organizations" subtitle="Create a team and invite people to act on its behalf with scoped roles." />

      <Panel title="New organization">
        <form className="flex flex-wrap items-end gap-2" onSubmit={create}>
          <input className={`${inputClass} max-w-xs flex-1`} maxLength={100} placeholder="Organization name" aria-label="Organization name" value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" disabled={busy || !name.trim()}>Create</Button>
        </form>
      </Panel>

      {orgs.length > 0 && <div className="flex justify-end"><ViewToggle view={view} onChange={setView} /></div>}

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : orgs.length === 0 ? (
        <p className="font-mono text-sm text-muted">You're not in any organizations yet.</p>
      ) : (
        <div className={view === "grid" ? "grid gap-3 md:grid-cols-2" : "grid gap-3"}>
          {pageItems.map((o) => (
            <button key={o.id} type="button" onClick={() => setSelId(o.id)} className="flex items-center gap-3 rounded-card border border-line bg-surface p-4 text-left transition-colors hover:border-accent">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-accent/15 font-display text-sm font-black text-accent-strong">{initials(o.name)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display font-black">{o.name}</span>
                <span className="font-mono text-[11px] text-muted">{o.roles.map((r) => ORG_ROLE_LABELS[r] ?? r).join(", ") || "member"}</span>
              </span>
              <Icon name="chevron" className="h-4 w-4 -rotate-90 text-muted" />
            </button>
          ))}
        </div>
      )}
      <Pagination page={page} pageCount={pageCount} onPage={setPage} />
    </div>
  );
}

function OrgDetail({ orgId, onBack }: { orgId: string; onBack: () => void }) {
  const [org, setOrg] = useState<Org | null>(null);
  const [myRoles, setMyRoles] = useState<string[]>([]);
  const [perms, setPerms] = useState<string[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [invites, setInvites] = useState<OrgInvite[]>([]);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    const g = await getOrg(orgId);
    setOrg(g.org);
    setMyRoles(g.my_roles);
    setPerms(g.my_permissions);
    const m = await getMembers(orgId);
    setMembers(m.members);
    setInvites(m.invites);
    setOwnerId(m.owner_account_id);
    setLoaded(true);
  }
  useEffect(() => { void load(); }, [orgId]);

  const canMembers = perms.includes("org.manage_members");
  const canRoles = perms.includes("org.manage_roles");
  const isOwner = myRoles.includes("owner");

  async function invite(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { invite_token } = await inviteMember(orgId, email.trim(), role);
      setInviteLink(`${window.location.origin}/orgs/accept?token=${invite_token}`);
      setEmail("");
      await load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-6">
      <button type="button" onClick={onBack} className="flex w-max items-center gap-1 font-mono text-sm font-bold text-accent-strong">
        <Icon name="chevron" className="h-4 w-4 rotate-90" /> Organizations
      </button>
      <PageHeader title={org?.name ?? "Organization"} subtitle="Members act on this org's behalf with their assigned roles." />

      {!loaded ? <p className="font-mono text-sm text-muted">Loading…</p> : (<>
      <Panel title={`Members (${members.length})`}>
        <ul className="grid">
          {members.map((m) => {
            const isOwner = m.account_id === ownerId;
            const primary = m.roles[0] ?? "member";
            return (
              <li key={m.account_id} className="flex flex-wrap items-center gap-3 border-t border-line py-3 first:border-t-0 first:pt-0">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent/15 font-display text-[11px] font-black text-accent-strong">{initials(m.email ?? "?")}</span>
                <span className="min-w-0 flex-1 truncate font-mono text-sm">{m.email ?? m.account_id.slice(0, 8)}</span>
                {canRoles && !isOwner ? (
                  <select className={`${inputClass} w-32`} value={primary} aria-label={`Role for ${m.email}`} onChange={async (e) => { await setMemberRoles(orgId, m.account_id, [e.target.value]); await load(); }}>
                    {ASSIGNABLE_ORG_ROLES.map((r) => <option key={r} value={r}>{ORG_ROLE_LABELS[r]}</option>)}
                  </select>
                ) : (
                  <span className="font-mono text-xs font-bold text-muted">{(isOwner ? "Owner" : ORG_ROLE_LABELS[primary]) ?? primary}</span>
                )}
                {canMembers && !isOwner && (
                  <Button variant="ghost" onClick={async () => { await removeMember(orgId, m.account_id); await load(); }}><span className="text-danger">Remove</span></Button>
                )}
              </li>
            );
          })}
        </ul>
      </Panel>

      {canMembers && (
        <Panel title="Invite a teammate">
          <form className="flex flex-wrap items-end gap-2" onSubmit={invite}>
            <input className={`${inputClass} max-w-xs flex-1`} type="email" placeholder="teammate@company.com" aria-label="Invite email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <select className={`${inputClass} w-32`} value={role} aria-label="Invite role" onChange={(e) => setRole(e.target.value)}>
              {ASSIGNABLE_ORG_ROLES.map((r) => <option key={r} value={r}>{ORG_ROLE_LABELS[r]}</option>)}
            </select>
            <Button type="submit" disabled={busy || !email.trim()}>Send invite</Button>
          </form>
          {inviteLink && (
            <div className="grid gap-1 rounded-lg border border-line bg-surface-2 p-3">
              <p className="font-mono text-xs text-muted">Share this single-use invite link (expires in 7 days):</p>
              <code className="block truncate font-mono text-xs text-accent-strong">{inviteLink}</code>
            </div>
          )}
          {invites.length > 0 && (
            <ul className="grid gap-1 border-t border-line pt-3 font-mono text-xs text-muted">
              {invites.map((i) => <li key={i.id}>Pending: {i.email} · {ORG_ROLE_LABELS[i.role] ?? i.role}</li>)}
            </ul>
          )}
        </Panel>
      )}

      {!isOwner && (
        <button type="button" onClick={async () => { await leaveOrg(orgId); onBack(); }} className="w-max font-mono text-xs font-bold text-danger">Leave this organization</button>
      )}
      </>)}
    </div>
  );
}
